import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { Section, SectionHead } from "@/components/landing/Section";
import { TrollArt } from "@/components/landing/TrollArt";
import { bySlug } from "@/lib/trolls";

const SEASONS = [
  { key: "winter", troll: "nordlysdronninga", img: "https://images.unsplash.com/photo-1669745356031-230d593866b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "spring", troll: "bekkefrua", img: "https://images.pexels.com/photos/31038013/pexels-photo-31038013.jpeg?auto=compress&cs=tinysrgb&w=1400" },
  { key: "summer", troll: "sol-sigrid", img: "https://images.unsplash.com/photo-1525642650051-4e79b2eecd7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { key: "autumn", troll: "myrmor", img: "https://images.unsplash.com/photo-1663459317278-fe33ad58b1b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
];

export default function Seasons() {
  const { t, tl } = useLang();
  const [active, setActive] = useState("winter");
  const s = SEASONS.find((x) => x.key === active);
  const copy = t.seasons.tabs[active];
  const troll = bySlug(s.troll);

  return (
    <Section id="seasons" testId="seasons-section">
      <SectionHead kicker={t.seasons.kicker} title={t.seasons.title} />
      <div className="flex flex-wrap gap-2 mt-10" role="tablist">
        {SEASONS.map(({ key }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            data-testid={`season-tab-${key}`}
            onClick={() => setActive(key)}
            className={`rounded-full px-5 py-2.5 font-display text-lg font-semibold transition-[background-color,color] duration-200 ${
              active === key ? "bg-amber text-parchment" : "text-ink-muted hover:text-ink bg-paper"
            }`}
          >
            {t.seasons.tabs[key].name}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35 }}
          className="grid lg:grid-cols-12 gap-6 mt-8"
          data-testid={`season-panel-${active}`}
        >
          <div className="lg:col-span-8 rounded-[20px] overflow-hidden border border-line shadow-journal">
            <img src={s.img} alt={copy.title} className="w-full h-[300px] md:h-[440px] object-cover" />
          </div>
          <div className="lg:col-span-4 flex flex-col gap-5">
            <div className="journal-card p-7 flex-1">
              <h3 className="font-display text-2xl font-semibold text-ink">{copy.title}</h3>
              <p className="text-ink-muted leading-relaxed mt-3">{copy.body}</p>
            </div>
            <div className="journal-card p-5 pb-4 flex items-end gap-4" data-testid={`season-troll-${active}`}>
              <TrollArt troll={troll} className="h-32 w-24 shrink-0" />
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-amber">{t.seasons.standout}</div>
                <div className="font-display text-xl font-semibold text-ink">{troll.name}</div>
                <div className="text-sm font-bold text-rowan">{tl(troll, "epithet")}</div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </Section>
  );
}
